import React, { FC } from 'react';
import styled from 'styled-components';

import {
  LevelEsencialVertical,
  LevelIntermediateVertical,
  LevelCompleteVertical,
  Clock2,
} from '@crehana/icons';
import {
  PrimaryButton,
  ProgressBar,
  LazyImage,
  TextTag,
  colors,
} from '@crehana/ui';
import { formatTime } from '@crehana/utils/dist/formatters';

import useOrganizationCourseTranslations from './hooks/useOrganizationCourseTranslations';

import { OrganizationCourseCardProps, levelType } from './types';

const { baseLighter } = colors;

const CustomClamp = styled.p<{ clamp: number }>`
  display: -webkit-box;
  -webkit-line-clamp: ${props => props.clamp};
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const renderLevelIcon = (level: levelType) => {
  if (level.originalId === 1) {
    return (
      <LevelEsencialVertical color={baseLighter} size={24} className="mr-8" />
    );
  }
  if (level.originalId === 2) {
    return (
      <LevelIntermediateVertical
        color={baseLighter}
        size={24}
        className="mr-8"
      />
    );
  }
  if (level.originalId === 3) {
    return (
      <LevelCompleteVertical color={baseLighter} size={24} className="mr-8" />
    );
  }
  if (level.originalId === 4) {
    return (
      <LevelCompleteVertical color={baseLighter} size={24} className="mr-8" />
    );
  }
};

const OrganizationCourseCard: FC<OrganizationCourseCardProps> = ({
  promoImage,
  title,
  isDark,
  duration,
  progress,
  url,
  level,
  className,
}) => {
  const { t } = useOrganizationCourseTranslations();
  const renderLabel = () => {
    if (progress === 100) {
      return t('SEE_AGAIN');
    }
    if (progress === 0) {
      return t('START_COURSE');
    }
    return t('CONTINUE_COURSE');
  };

  return (
    <div className="shadow-tooltip">
      <div className="relative">
        <div className="absolute z-1" style={{ top: 12, left: 12 }}>
          <TextTag label={t('OWN_COURSE')} />
        </div>
        <a href={url}>
          <LazyImage
            className="w-full h-160 cursor-pointer"
            imgClassName="object-cover"
            src={promoImage}
            imgxParams={{ h: 160, dpr: 2, crop: 'faces' }}
            alt={title}
          />
        </a>
      </div>

      <ProgressBar
        progress={progress || 0}
        darkMode={isDark}
        className="w-full"
      />
      <div
        className={`p-16 ${isDark ? 'bg-base-lighter-16' : ''} ${className}`}
      >
        <div className="mb-16">
          <a href={url}>
            <CustomClamp
              className={`font-subtitle3 h-40 mb-8 cursor-pointer ${
                isDark ? 'text-white' : 'text-base-main'
              }`}
              clamp={2}
            >
              {title}
            </CustomClamp>
          </a>
          <div className="flex items-center mb-16">
            {level && (
              <div className="flex items-center">
                {renderLevelIcon(level)}
                <p
                  className={`font-body4 ${
                    isDark ? 'text-white' : 'text-gray-dark'
                  }`}
                >
                  {t('LEVEL')} {level.name}
                </p>
              </div>
            )}
          </div>
          {!!duration && (
            <div className="flex items-center mb-16">
              <Clock2 color={baseLighter} size={18} className="mr-8" />
              <p
                className={`font-body4 ${
                  isDark ? 'text-white' : 'text-base-main'
                }`}
              >
                {t('DURATION')} {formatTime(duration)}
              </p>
            </div>
          )}
        </div>
        <PrimaryButton
          full
          label={renderLabel()}
          href={url}
          darkMode={isDark}
        />
      </div>
    </div>
  );
};

export default OrganizationCourseCard;
